import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount } from 'vue'
    import { useId, useRoute } from '@common/composables'
    import { useSurveyPage } from '@RankHR/Regions/pages-core'

    
export default /*#__PURE__*/_defineComponent({
  __name: 'SurveyPage',
  setup(__props) {

    const questionsContainerTop = ref(null)

    const ticksToSave = 5
    let tickHandle = null
    let tick = ref(ticksToSave)

    let input = ref({
        QuestionsGrouping: null,
        SelectedPersonEmail: null,
        SelectedCriterionPath: null,
    })

    const id = useId()

    const { usePersonVotedCommand, useSurveyByPersonQuery } = useSurveyPage()

    const { invokePersonVotedCommandV1, succeed, invoking, command } = usePersonVotedCommand(id)

    const { modelSurveyByPersonQueryV1, runSurveyByPersonQueryV1, authorized, redirectUrl } = useSurveyByPersonQuery(id)

    const model = computed(() => modelSurveyByPersonQueryV1.value)
    const personDisplayName = computed(() => model.value?.PersonDisplayName)

    const allQuestions = computed(() => (model.value?.Questions || [])
        .map(q => ref({ ...q, ...{ InputValue: q.Value, InputComment: q.Comment } }).value))

    const peopleEmails = computed(() => model.value?.PeopleEmails || [])
    const peopleDisplayNames = computed(() => model.value?.PeopleDisplayNames || [])
    const peopleCriteriaNames = computed(() => model.value?.PeopleCriteriaNames || [])
    const criteriaPaths = computed(() => model.value?.CriteriaPaths || [])

    const groupByPerson = computed(() => input.value.QuestionsGrouping == 'ByPerson')
    const groupByCriterion = computed(() => input.value.QuestionsGrouping == 'ByCriterion')
    const groupByMissingComments = computed(() => input.value.QuestionsGrouping == 'ByMissingComments')

    const questions = computed(() => {
        if (groupByPerson.value && input.value.SelectedPersonEmail) {
            return allQuestions.value.filter(a => a.PersonEmail == input.value.SelectedPersonEmail)
        }
        else if (groupByCriterion.value && input.value.SelectedCriterionPath) {
            return allQuestions.value.filter(a => a.CriterionPath == input.value.SelectedCriterionPath)
        }
        else if (groupByMissingComments.value && questionsWithCommentMissing.value.length) {
            return questionsWithCommentMissing.value
        }
        return []
    })

    const questionsTitle = computed(() => {
        if (groupByPerson.value && input.value.SelectedPersonEmail) {
            const idx = peopleEmails.value.indexOf(input.value.SelectedPersonEmail);
            const displayName = peopleDisplayNames.value[idx];
            const cirteriaName = peopleCriteriaNames.value[idx];
            return `Score ${displayName} (${cirteriaName}) in the context of criteria below:`;
        }
        else if (groupByCriterion && input.value.SelectedCriterionPath) {
            return `Score people below in the context of ${input.value.SelectedCriterionPath}:`;
        }
        else if (groupByMissingComments && questionsWithCommentMissing.value.length) {
            return `Please add your comments where it is required, but missing:`;
        }
        return "No questions to display";
    })

    const changedQuestions = computed(() => allQuestions.value.filter(a => a.Value != a.InputValue || a.Comment != a.InputComment))

    const questionsWithCommentRequired = computed(() => changedQuestions.value.filter(a => isCommentRequired(a)))

    const questionsWithCommentMissing = computed(() => allQuestions.value.filter(a => isCommentMissing(a)))

    const progress = computed(() => allQuestions.value.filter(a => a.Value != null || a.InputValue != null).length)

    const progressPercent = computed(() => Math.round(progress.value / allQuestions.value.length * 100))

    const saveBtnTitle = computed(() => `*Save (${changedQuestions.value.length})`)

    watch(authorized, () => {
        if (!authorized.value) {
            location.href = redirectUrl.value
        }
    })

    watch(model, () => {
        input.value.QuestionsGrouping = 'ByPerson';
        document.title = `Survey for ${personDisplayName.value}`;
    })

    watch(groupByPerson, (value) => {
        if (value) {
            input.value.SelectedPersonEmail = peopleEmails.value[0];
        }
        else {
            input.value.SelectedPersonEmail = null;
        }
    })

    watch(groupByCriterion, (value) => {
        if (value) {
            input.value.SelectedCriterionPath = criteriaPaths.value[0];
        }
        else {
            input.value.SelectedCriterionPath = null;
        }
    })

    watch(succeed, () => {
        if (succeed.value) {
            if (command.value.Votes) {
                for (let v of command.value.Votes) {
                    const q = allQuestions.value.find(a => a.CriterionPath == v.CriterionPath && a.PersonId == v.PersonId);

                    if (q) {
                        q.Value = v.Value;
                        q.Comment = v.Comment;
                    }
                }
            }
        }
    })

    onMounted(() => {
        runSurveyByPersonQueryV1()

        tickHandle = setInterval(() => {
            if (tick.value == 0) {
                tick.value = ticksToSave;
                save();
            }
            else {
                tick.value--;
            }
        }, 1000);
    })

    onBeforeMount(() => {
        window.addEventListener("beforeunload", ensureSavedOnExit)
    })

    onBeforeUnmount(() => {
        window.removeEventListener("beforeunload", ensureSavedOnExit);

        if (tickHandle) {
            clearInterval(tickHandle);
        }
    })

    function scrollToTop() {
        if (questionsContainerTop.value) {
            questionsContainerTop.value.scrollIntoView({ behavior: 'smooth' });
        }
    }

    function save() {
        if (!invoking.value && changedQuestions.value.length > 0) {
            invokePersonVotedCommandV1({
                Votes: changedQuestions.value.map(q => ({
                    CriterionPath: q.CriterionPath,
                    VotingCycle: q.VotingCycle,
                    PersonId: q.PersonId,
                    Value: q.InputValue,
                    Comment: q.InputComment,
                }))
            })
        }
    }

    function getProgressByPerson(personEmail) {
        const questions = allQuestions.value.filter(a => a.PersonEmail == personEmail);
        const missing = questions.filter(a => a.InputValue === null);
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function getProgressByCriterion(criterionPath) {
        const questions = allQuestions.value.filter(a => a.CriterionPath == criterionPath);
        const missing = questions.filter(a => a.InputValue === null);
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function getProgressByMissingComments() {
        const questions = allQuestions.value
        const missing = questions.filter(a => isCommentMissing(a));
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function isCommentRequired(q) {
        return (q.InputValue == 1 || q.InputValue == 2) && (!(q.InputComment || '').trim());
    }

    function isCommentMissing(q) {
        return (q.InputValue == 1 || q.InputValue == 2) && (!(q.Comment || '').trim());
    }

    function ensureSavedOnExit(event) {
        if (changedQuestions.value.length == 0) return;
        event.preventDefault();
        event.returnValue = "";
    }

    function handleSave() {
        if (questionsWithCommentRequired.value.length > 0) {
            alert('Please provide comments for questions where you entered "Does not meet.." or "Partially meets..."');
            return;
        }
        save();
    }

    function resetTicker() {
        tick.value = ticksToSave;
    }

return { __sfc: true,questionsContainerTop, ticksToSave, tickHandle, tick, input, id, usePersonVotedCommand, useSurveyByPersonQuery, invokePersonVotedCommandV1, succeed, invoking, command, modelSurveyByPersonQueryV1, runSurveyByPersonQueryV1, authorized, redirectUrl, model, personDisplayName, allQuestions, peopleEmails, peopleDisplayNames, peopleCriteriaNames, criteriaPaths, groupByPerson, groupByCriterion, groupByMissingComments, questions, questionsTitle, changedQuestions, questionsWithCommentRequired, questionsWithCommentMissing, progress, progressPercent, saveBtnTitle, scrollToTop, save, getProgressByPerson, getProgressByCriterion, getProgressByMissingComments, isCommentRequired, isCommentMissing, ensureSavedOnExit, handleSave, resetTicker }
}

})